// Текст гайда. Ссылки — адреса экранов приложения; тест сверяет их с объявленными маршрутами.

import type { IconName } from '@/components/ui/Icon'

export type GuideLink = { to: string; label: string }

export type GuideStep = { title: string; text: string; link: GuideLink }

export type GuideSection = {
  id: string
  title: string
  icon: IconName
  /** Одна строка: зачем этот раздел. */
  summary: string
  /** Что здесь можно сделать. */
  points: string[]
  links: GuideLink[]
}

export type GuideTerm = { term: string; text: string }

/** Три мысли, с которыми проще разобраться во всём остальном. */
export const IDEAS: { icon: IconName; title: string; text: string }[] = [
  {
    icon: 'today',
    title: '«Сегодня» — главный экран',
    text: 'План на день, совет ИИ, сон, готовность, вес и добавки — на одном экране. Отсюда начинается почти всё.',
  },
  {
    icon: 'activity',
    title: 'Часть данных приходит сама',
    text: 'Сон, шаги, пульс и активности приходят из Garmin. Вес, давление, самочувствие и подходы вводишь ты — обычно в пару касаний.',
  },
  {
    icon: 'history',
    title: 'Ничего не сломаешь',
    text: 'Любое изменение — твоё, ИИ или бота — попадает в журнал и откатывается одной кнопкой. Пробуй смело.',
  },
]

export const FIRST_STEPS: GuideStep[] = [
  {
    title: 'Подключи Garmin',
    text: 'Сон, шаги, пульс и активности начнут приходить сами. Пароль нужен один раз и не сохраняется.',
    link: { to: '/settings/garmin', label: 'Настройки Garmin' },
  },
  {
    title: 'Внеси вес',
    text: 'Вручную или импортом экспорта из Zepp Life — история взвешиваний подтянется целиком.',
    link: { to: '/health/weight', label: 'Вес' },
  },
  {
    title: 'Создай шаблон тренировки',
    text: 'Например, «Верх А»: упражнения по порядку, целевые подходы, отдых и шаг прогрессии.',
    link: { to: '/templates', label: 'Шаблоны' },
  },
  {
    title: 'Задай расписание',
    text: 'В какой день недели какая тренировка. План на 4 недели вперёд появится и будет продлеваться сам.',
    link: { to: '/plan/rules', label: 'Расписание' },
  },
  {
    title: 'Поставь цель',
    text: 'Вес к дате, шаги, сон, тренировки в неделю или рабочий вес в упражнении — прогресс считается сам.',
    link: { to: '/teloscope/goals', label: 'Цели' },
  },
  {
    title: 'Установи на телефон и включи уведомления',
    text: 'Android: меню браузера → «Установить приложение». iPhone: Safari → «Поделиться» → «На экран „Домой“». Потом включи push.',
    link: { to: '/settings/notifications', label: 'Уведомления' },
  },
  {
    title: 'Проведи первую тренировку',
    text: 'По плану, из шаблона, с ИИ или пустую. Подходы отмечаются одним касанием, таймер отдыха включается сам.',
    link: { to: '/workout', label: 'Тренировка' },
  },
]

export const DAY: GuideStep[] = [
  {
    title: 'Утром',
    text: 'Открой «Сегодня»: совет ИИ за кнопкой «!» вверху — делать по плану, облегчить или отдохнуть, — сон и готовность. Взвесься через «Быстрый ввод».',
    link: { to: '/', label: 'Сегодня' },
  },
  {
    title: 'В зале',
    text: '«Тренировка» — открой плановую, нажми «Старт», отмечай подходы. Сеть не нужна: всё уйдёт на сервер, когда появится связь.',
    link: { to: '/workout', label: 'Тренировка' },
  },
  {
    title: 'Днём',
    text: 'Отметь принятые добавки и самочувствие — прямо на «Сегодня». Если что-то болит, запиши травму: план и советы её учтут.',
    link: { to: '/health/wellbeing', label: 'Самочувствие' },
  },
  {
    title: 'Вечером',
    text: 'Итог питания за день и давление — через «Быстрый ввод» или в разделе «Здоровье».',
    link: { to: '/health/nutrition', label: 'Питание' },
  },
  {
    title: 'Раз в неделю',
    text: 'В понедельник готов недельный отчёт ИИ. Загляни в «Нагрузку»: не пора ли разгрузиться.',
    link: { to: '/ai/reports', label: 'Отчёты' },
  },
]

export const SECTIONS: GuideSection[] = [
  {
    id: 'today',
    title: 'Сегодня',
    icon: 'today',
    summary: 'Сводка дня: что запланировано и как ты сейчас.',
    points: [
      'План на сегодня — кнопка «Открыть» показывает тренировку; начинается она кнопкой «Старт».',
      'Сон, готовность и Body Battery, шаги, цели, активные травмы.',
      'Кнопки вверху: «!» — совет ИИ на сегодня (точка — новый, ещё не открыт), ⟳ — синхронизировать Garmin сейчас, не дожидаясь получасовой.',
      'Отметка принятых добавок одним касанием.',
      '«Быстрый ввод» вверху: вес, давление, самочувствие или питание.',
      'Карточка «Начни отсюда» — чек-лист первых шагов; когда не нужна, скрой её.',
    ],
    links: [{ to: '/', label: 'Сегодня' }],
  },
  {
    id: 'workout',
    title: 'Тренировка',
    icon: 'workout',
    summary: 'Тренировка в зале — с телефона, даже без сети.',
    points: [
      'Открой по плану, из шаблона или пустую — до «Старт» её можно смотреть и править (упражнения, подходы, вес), секундомер стоит и на сервер ничего не уходит; упражнения можно добавлять и по ходу.',
      'Неначатая тренировка ждёт на экране «Тренировка» до «Старт» или «Отменить»; подготовленная не сегодня забывается сама.',
      '«С ИИ» — разовая тренировка: выбери, на что, сколько минут и как тяжело, допиши пожелания — ИИ соберёт её из справочника с учётом самочувствия, травм и прошлых дней. Посмотри, замени незнакомое упражнение и открой; вес подставится из прошлого раза.',
      'Поля подхода уже заполнены по плану, рядом — что было в прошлый раз.',
      'Отметка подхода запускает таймер отдыха; ±15 секунд — кнопками.',
      'Разминочные подходы не идут в объём и рекорды.',
      '«С ИИ» рядом с «Добавить упражнение» — правка словами: «добавь 2 упражнения на бицепс», «убавь подходы у оставшихся». ИИ покажет правки, «Применить» перенесёт их; упражнения с выполненными подходами он не меняет.',
      'Закрывай кнопкой «Завершить тренировку» — тогда плановая отметится выполненной.',
    ],
    links: [{ to: '/workout', label: 'Тренировка' }],
  },
  {
    id: 'workouts',
    title: 'История тренировок',
    icon: 'history',
    summary: 'Все прошедшие тренировки и ввод задним числом.',
    points: [
      'Карточка тренировки: подходы, тоннаж и привязанная активность Garmin.',
      'Любую тренировку можно поправить или удалить.',
      'Забыл записать — внеси прошедшую тренировку задним числом.',
    ],
    links: [
      { to: '/workouts', label: 'История' },
      { to: '/workouts/new', label: 'Внести прошедшую' },
    ],
  },
  {
    id: 'templates',
    title: 'Шаблоны',
    icon: 'list',
    summary: 'Заготовки силовых тренировок.',
    points: [
      'Упражнения по порядку, целевые подходы и отдых; у упражнения — группа мышц и «Техника», длинный шаблон удобно сворачивать.',
      '«С ИИ» — правка шаблона словами: добавить, убрать, заменить упражнение, поменять подходы или порядок. Правки видно до «Применить», в шаблон они попадут по «Сохранить».',
      'Прогрессия: если все рабочие подходы сделаны на целевые повторы, в следующий раз вес вырастет на шаг.',
      'Из шаблона начинают тренировку и строят расписание.',
    ],
    links: [{ to: '/templates', label: 'Шаблоны' }],
  },
  {
    id: 'exercises',
    title: 'Упражнения',
    icon: 'activity',
    summary: 'Справочник упражнений и рекорды по каждому.',
    points: [
      'Группы мышц, оборудование и сопоставление с Garmin — чтобы на часах было нужное упражнение.',
      'Фильтр по нескольким группам мышц — списком или нажатием на мышцы фигуры; ⓘ у упражнения — техника и схема.',
      'Нет нужного — добавь своё.',
      'Карточка упражнения: рекорды, расчётный 1ПМ и история подходов.',
      '«Что качает»: фигура подсвечивает основную мышцу оранжевым, вспомогательные — янтарным.',
    ],
    links: [{ to: '/exercises', label: 'Упражнения' }],
  },
  {
    id: 'character',
    title: 'Персонаж',
    icon: 'character',
    summary: 'Прокачка по-настоящему: уровень, мышцы на фигуре и S.P.E.C.I.A.L.',
    points: [
      'Опыт: 50 XP за тренировку и 5 за рабочий подход — растёт уровень.',
      'После тренировки в её карточке — итог: сколько XP получено, новый ли уровень и какие мышцы работали.',
      'Фигура: мышцы последней тренировки или нагрузка за неделю; синие давно не работали.',
      'S.P.E.C.I.A.L. считается по силе, восстановлению, VO2max, составу тела, плану, балансу и регулярности — без них медленно падает.',
      'Пол фигуры — в профиле; не задан — берётся из InBody.',
      'Маскот в углу экрана: после тренировки радуется «+XP», по нажатию — уровень, подсказка и переход сюда. Спрятать и вернуть — галочкой на этой странице.',
    ],
    links: [{ to: '/character', label: 'Персонаж' }],
  },
  {
    id: 'plan',
    title: 'План',
    icon: 'plan',
    summary: 'Что и когда тренировать.',
    points: [
      'Календарь: плановые тренировки по дням — начать, перенести, пропустить или отправить на часы Garmin.',
      '«Разовая» — тренировка на один день вне расписания.',
      'Расписание: правила по дням недели, из них строится план на 4 недели.',
      'Нагрузка: подходы по неделям и группам мышц, статус Garmin и предложения разгрузки — вес меняется только с твоего согласия.',
    ],
    links: [
      { to: '/plan/calendar', label: 'Календарь' },
      { to: '/plan/rules', label: 'Расписание' },
      { to: '/plan/load', label: 'Нагрузка' },
    ],
  },
  {
    id: 'health',
    title: 'Здоровье',
    icon: 'health',
    summary: 'Всё, что меряешь и отмечаешь сам.',
    points: [
      'Вес — с графиком и импортом из Zepp Life; Замеры — обхваты и свои показатели.',
      'Шаги — график с целью Garmin, серии дней с выполненной целью и рекорды, календарь за год (万 — дни от 10 000 шагов) и ритм недели.',
      'Состав тела — замеры InBody из PDF-отчёта DDX: мышцы, жир, вода, сегменты и нормы.',
      'Как отправить отчёт из DDX: на Android открой отчёт в DDX → «Поделиться» → Telos; на компьютере — «Загрузить отчёт» на вкладке; с iPhone — быстрой командой (инструкция на вкладке). Перед сохранением проверь цифры на экране сверки.',
      'Давление — значения от 140/90 помечаются.',
      'Самочувствие — оценка от 1 до 5 раз в день, травмы и болезни.',
      'Анализы — показатели с нормами и график между сдачами.',
      'Добавки — курсы и отметка приёма; Питание — итог дня: калории, БЖУ, вода.',
    ],
    links: [
      { to: '/health/weight', label: 'Вес' },
      { to: '/health/measurements', label: 'Замеры' },
      { to: '/health/steps', label: 'Шаги' },
      { to: '/health/inbody', label: 'Состав тела' },
      { to: '/health/pressure', label: 'Давление' },
      { to: '/health/wellbeing', label: 'Самочувствие' },
      { to: '/health/labs', label: 'Анализы' },
      { to: '/health/supplements', label: 'Добавки' },
      { to: '/health/nutrition', label: 'Питание' },
    ],
  },
  {
    id: 'garmin-data',
    title: 'Сон и активности',
    icon: 'chart',
    summary: 'Данные Garmin.',
    points: [
      'Сон: длительность, фазы и оценка; день ночи — день пробуждения.',
      'Активности: пульс, зоны, калории, нагрузка. Сама привязывается к тренировке, если совпала по времени.',
    ],
    links: [
      { to: '/sleep', label: 'Сон' },
      { to: '/activities', label: 'Активности' },
    ],
  },
  {
    id: 'teloscope',
    title: 'Телоскоп',
    icon: 'target',
    summary: 'Графики любых метрик и цели — к чему идёшь и насколько близко.',
    points: [
      'Показатели: любая метрика за период, вторую можно наложить для сравнения.',
      'Цели: вес к дате, шаги в день, сон, тренировки в неделю или вес в упражнении.',
      'Прогресс целей считается из твоих данных сам и виден на «Сегодня»; достигнутые уходят в архив.',
    ],
    links: [
      { to: '/teloscope/metrics', label: 'Показатели' },
      { to: '/teloscope/goals', label: 'Цели' },
    ],
  },
  {
    id: 'ai',
    title: 'ИИ',
    icon: 'ai',
    summary: 'Помощник, который видит твои данные.',
    points: [
      'Чат: спроси про сон, вес, рекорды или тренировки. В чате ИИ ничего не записывает.',
      'Отчёты: советы на каждый день и недельные отчёты по понедельникам.',
      'Составить план: опиши цель и ограничения — ИИ предложит шаблоны и расписание. Применяется только по кнопке, откат — в журнале. Одну тренировку на сегодня ИИ соберёт на экране «Тренировка».',
      'Это подсказки, а не медицинская рекомендация.',
    ],
    links: [
      { to: '/ai/chat', label: 'Чат' },
      { to: '/ai/reports', label: 'Отчёты' },
      { to: '/ai/plan', label: 'Составить план' },
    ],
  },
  {
    id: 'changes',
    title: 'Журнал',
    icon: 'history',
    summary: 'История всех изменений данных.',
    points: [
      'Кто изменил — ты, ИИ, бот через MCP, синхронизация или импорт, — что, когда, было и стало.',
      'Любую запись можно откатить; сам откат тоже попадает в журнал.',
    ],
    links: [{ to: '/changes', label: 'Журнал' }],
  },
  {
    id: 'settings',
    title: 'Настройки',
    icon: 'settings',
    summary: 'Профиль, подключения, уведомления и безопасность.',
    points: [
      'Garmin — вход, код подтверждения и журнал синхронизаций.',
      'Уведомления — какие и во сколько: совет готов, тренировка по плану, пора взвеситься, недельный отчёт.',
      'MCP — ключи для своего бота, n8n или Claude Code и доступы Claude.ai/ChatGPT.',
      'ИИ — действующие модели, источник настроек, месячный бюджет и расход; время совета и недельного отчёта. Адреса, модели и бюджет назначает администратор; пустая личная настройка наследуется.',
      'Данные — выгрузка своих записей в один JSON-файл. Общие резервные копии доступны администратору в настройках сервиса.',
      'Профиль — имя, email и роль аккаунта, смена email с подтверждением. Здесь же можно сменить пароль, включить или перевыпустить 2FA и получить новые коды восстановления; эти действия доступны и во вкладке «Безопасность».',
      'У администратора 2FA обязательна, у обычного пользователя — по выбору. Ключ доступа позволяет входить без пароля; включённая 2FA остаётся вторым шагом.',
      'Аккаунты приходят по приглашению администратора. Каждый видит свои записи и подключения. При смене аккаунта неотправленная тренировка сохраняется отдельно и продолжит отправку после входа прежнего владельца.',
    ],
    links: [
      { to: '/settings/profile', label: 'Профиль' },
      { to: '/settings/garmin', label: 'Garmin' },
      { to: '/settings/notifications', label: 'Уведомления' },
      { to: '/settings/mcp', label: 'MCP' },
      { to: '/settings/ai', label: 'ИИ' },
      { to: '/settings/data', label: 'Данные' },
      { to: '/settings/security', label: 'Безопасность' },
    ],
  },
  {
    id: 'users',
    title: 'Администрирование',
    icon: 'shield',
    summary: 'Пользователи, ИИ и общие настройки сервиса в отдельных вкладках.',
    points: [
      'Пользователи — приглашения по email и редактирование аккаунта в боковой панели: роль, блокировка, сброс пароля или 2FA и счётчики данных.',
      'ИИ — общие и персональные модели LLM/Jev, месячные бюджеты и агрегированные расходы без содержимого личных записей.',
      'Настройки сервиса — состояние окружения и внешних подключений, резервные копии и история их выполнения.',
    ],
    links: [{ to: '/admin/users', label: 'Пользователи' }, { to: '/admin/ai', label: 'ИИ' }, { to: '/admin/settings', label: 'Настройки сервиса' }],
  },
]

export const TERMS: GuideTerm[] = [
  { term: 'Тренировка', text: 'то, что было сделано на самом деле: начало, конец и подходы.' },
  { term: 'Подход', text: 'одно выполнение упражнения: вес и повторы (или время, дистанция). Разминочный не идёт в объём и рекорды.' },
  { term: 'Шаблон', text: 'заготовка тренировки: упражнения, целевые подходы и отдых.' },
  { term: 'Плановая тренировка', text: 'тренировка, назначенная на конкретный день: запланирована, выполнена, пропущена или не проведена.' },
  { term: 'Правило расписания', text: '«в этот день недели — такая тренировка»; из правил сами появляются плановые.' },
  { term: 'Предписание', text: 'целевые подходы на сегодня с уже применённой прогрессией.' },
  { term: 'Прогрессия', text: 'правило роста веса в упражнении шаблона, когда все рабочие подходы выполнены.' },
  { term: 'Разгрузка', text: 'предложение снизить вес на 10 % после трёх тренировок подряд с невыполненными повторами.' },
  { term: 'Совет на сегодня', text: 'утренний ответ ИИ: делать по плану, облегчить или отдохнуть — и почему.' },
  { term: 'Откат', text: 'отмена одного изменения из журнала: созданное удаляется, удалённое возвращается.' },
]
